import { Strings } from "--client/../strings/en"
import { PopperSurface, PopperSurfaceProps } from "--floponents-web/molecules/PopperSurface";
import { ListEntryIcon } from "--floponents-web/atoms/ListEntry";
import { ListEntryText } from "--floponents-web/atoms/ListEntry";
import { ListEntry } from "--floponents-web/atoms/ListEntry";
import { useBreakpoint } from "--components/hooks/useBreakpoint";
import { Body2 } from "--components/typography";

import { ListSurface } from "--floponents-web/molecules/ListEntries/ListSurface/ListSurface"
import * as React from "react"
import styled, { css } from "styled-components"
import { ErrorBoundary } from "../../common/components/ErrorBoundary"
import { AutoCompleteResult } from "./useAutoCompleteResults"
import { TypedSlateAutoCompleteEditor } from "../editors/useAutoCompleteEditor"
import { BaseElementT } from "--root/slate/types"

const StyledAutoCompleteResults = styled.div<{ mobile: boolean }>`
	${props => props.mobile && css`width:100vw;` || css`max-width:50vw;`}
	max-height: 33vh;
	overflow:auto;
	padding-right:${props => props.theme.spacing(1)};
`

interface AutoCompleteSearchResultsProps<T extends BaseElementT<any>, RES_T extends T> {
    results: Map<string, AutoCompleteResult<RES_T>>,
    editor: TypedSlateAutoCompleteEditor<T>,
}

export interface AutoCompleteSearchResultsPopperProps<T extends BaseElementT<any>, RES_T extends T> extends AutoCompleteSearchResultsProps<T, RES_T> {

}


export function AutoCompleteSearchResultsPopper<T extends BaseElementT<any>, RES_T extends T>(props: AutoCompleteSearchResultsPopperProps<T, RES_T>) {
    const { ...rest } = props
    const [popperOpen, setPopperOpen] = React.useState(true);
    React.useEffect(() => {
        setPopperOpen(!!props.results.size);
    }, [!!props.results.size, Array.from(props.results.values())[0]?.matchedText]);
    const closePopper = React.useCallback(() => setPopperOpen(false), []);
    if (popperOpen) {
        const caret = props.editor.getCaretPosition();
        if (caret) {
            const inTopHalf = caret.getBoundingClientRect().y < window.innerHeight / 2
            const inLeftHalf = caret.getBoundingClientRect().x < window.innerWidth / 2
            const placement: PopperSurfaceProps["placement"] = inTopHalf
                ? inLeftHalf
                    ? "bottom-start"
                    : "bottom-end"
                : inLeftHalf
                    ? "top-start"
                    : "top-end"
            return <PopperSurface onClose={closePopper} triggerRef={caret} open={popperOpen} menu placement={placement}>
                <AutoCompleteSearchResults<T, RES_T> {...rest} />
            </PopperSurface>
        }
    }
    return null
}

type IconListEntryProps = {
    icon: React.ReactNode
    primary: React.ReactNode,
    secondary?: React.ReactNode
    active?: boolean
}

const StandardListEntry = React.forwardRef((props: IconListEntryProps, ref: React.RefObject<HTMLDivElement>) => {
    const { icon, primary, secondary, ...containerProps } = props;
    return <ListEntry indentStyle="dense" ref={ref} {...containerProps}>
        {icon && <ListEntryIcon style={{ textAlign: "center" }}>{icon}</ListEntryIcon>}
        <ListEntryText noWrap primary={primary} secondary={secondary} />
    </ListEntry>
});


export function AutoCompleteSearchResults<T extends BaseElementT<any>, R extends T>(props: AutoCompleteSearchResultsProps<T, R>) {
    const [bp, orientation] = useBreakpoint()
    return <StyledAutoCompleteResults mobile={bp === "mobile" && orientation !== "landscape"}>
        <ErrorBoundary fallback={errProps => { return <Body2>{Strings.errors.genericTitle} {errProps.error || "Unknown type"}</Body2> }}>
            <ListSurface
                tabSelects
                onSelectedItem={async idx => {
                    const item = Array.from(props.results.values())[idx];
                    if (item) {
                        if (item.insertFunction)
                            item.insertFunction(item)
                        else
                            props.editor.insertAutoCompleteNode(item)
                    }
                }}>
                {Array.from(props.results).map(([k, p]) =>
                    p.jsx
                    || <StandardListEntry key={k} primary={p.primary || (p.node.children ? p.node.children[0].text : p.node.text)} secondary={p.secondary} icon={p.icon} />
                )}
            </ListSurface>
        </ErrorBoundary>
    </StyledAutoCompleteResults>
}

